/**
 * The small pieces, in one file.
 *
 * Icons are inline SVG from one family (Lucide's geometry, 1.75px
 * stroke) rather than an icon dependency or emoji: emoji render
 * differently on every platform and cannot take a colour token.
 *
 * Every status chip carries a label as well as a colour. Colour alone is
 * not a signal - it fails for a colourblind reader, in greyscale, and in
 * forced-colours mode.
 */

import type { ReactNode } from "react";

type IconProps = { size?: number; className?: string };

function svg(path: ReactNode, { size = 16, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

export const Icon = {
  alert: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      </>,
      p,
    ),
  check: (p: IconProps = {}) => svg(<path d="M20 6 9 17l-5-5" />, p),
  trash: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M3 6h18" />
        <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
      </>,
      p,
    ),
  clock: (p: IconProps = {}) =>
    svg(
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>,
      p,
    ),
  file: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7Z" />
        <path d="M14 2v5h5" />
      </>,
      p,
    ),
  upload: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="m7 10 5-5 5 5" />
        <path d="M12 5v12" />
      </>,
      p,
    ),
  download: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="m7 10 5 5 5-5" />
        <path d="M12 15V3" />
      </>,
      p,
    ),
  refresh: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
        <path d="M3 21v-5h5" />
      </>,
      p,
    ),
  left: (p: IconProps = {}) => svg(<path d="m15 18-6-6 6-6" />, p),
  right: (p: IconProps = {}) => svg(<path d="m9 18 6-6-6-6" />, p),
  search: (p: IconProps = {}) =>
    svg(
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </>,
      p,
    ),
  external: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M15 3h6v6" />
        <path d="M10 14 21 3" />
        <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
      </>,
      p,
    ),
  target: (p: IconProps = {}) =>
    svg(
      <>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="3" />
      </>,
      p,
    ),
  logo: ({ size = 22 }: IconProps = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" fill="#0f172a" />
      <path d="M7 8h6M7 12h10M7 16h4" stroke="#f8fafc" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="17" cy="16" r="2.6" fill="#2563eb" stroke="#f8fafc" strokeWidth="1.2" />
    </svg>
  ),
};

export type Tone = "neutral" | "danger" | "warn" | "ok" | "info";

export function Chip({
  tone = "neutral",
  icon,
  children,
}: {
  tone?: Tone;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className={`chip ${tone}`}>
      {icon}
      {children}
    </span>
  );
}

/** The state of one reading, as a labelled chip. */
export function StatusChip({ status, ok }: { status: string; ok: boolean }) {
  if (!ok) {
    return (
      <Chip tone="danger" icon={Icon.alert({ size: 13 })}>
        Unreadable
      </Chip>
    );
  }
  if (status === "needs_review") {
    return (
      <Chip tone="warn" icon={Icon.alert({ size: 13 })}>
        Needs review
      </Chip>
    );
  }
  if (status === "approved") {
    return (
      <Chip tone="ok" icon={Icon.check({ size: 13 })}>
        Approved
      </Chip>
    );
  }
  // "Passed", not "Clean": a document can carry warnings and still
  // not need review, and calling that clean contradicts the
  // findings column on the same row.
  return <Chip tone="neutral">Passed</Chip>;
}

export function Tile({
  label,
  value,
  foot,
  tone,
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
  tone?: "danger" | "warn" | "ok" | "accent";
}) {
  return (
    <dl className={`tile${tone ? ` ${tone}` : ""}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
      {foot && <div className="foot">{foot}</div>}
    </dl>
  );
}
